export function createWebGLSafety({ React, THREE }) {
  const localFaultsAllowed =
    import.meta.env.DEV
    || ["127.0.0.1", "localhost", "::1"].includes(window.location.hostname);

  function faultMode() {
    if (!localFaultsAllowed) return null;
    return new URLSearchParams(window.location.search).get("ni-fault");
  }

  function VisualFallback({ className = "", style, children, label }) {
    return (
      <div
        className={`ni-visual-fallback ${className}`.trim()}
        style={{
          position: "absolute",
          inset: 0,
          overflow: "hidden",
          background:
            "radial-gradient(circle at 50% 35%, rgba(168,164,255,.13), transparent 45%), linear-gradient(145deg, rgba(255,255,255,.035), rgba(255,255,255,.008))",
          ...style,
        }}
        role={label ? "img" : undefined}
        aria-label={label}
        aria-hidden={label ? undefined : "true"}
      >
        <span
          aria-hidden="true"
          style={{
            position: "absolute",
            inset: 0,
            opacity: 0.42,
            backgroundImage:
              "linear-gradient(rgba(255,255,255,.05) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,.05) 1px, transparent 1px)",
            backgroundSize: "28px 28px",
            maskImage: "radial-gradient(ellipse at 50% 45%, black 10%, transparent 76%)",
          }}
        />
        {children}
      </div>
    );
  }

  class VisualErrorBoundary extends React.Component {
    constructor(props) {
      super(props);
      this.state = { failed: false };
    }

    static getDerivedStateFromError() {
      return { failed: true };
    }

    componentDidCatch(error, info) {
      this.props.onError?.(error, info);
      if (import.meta.env.DEV) {
        console.error(`[visual:${this.props.name || "unknown"}]`, error);
      }
    }

    render() {
      if (this.state.failed) {
        return this.props.fallback || <VisualFallback />;
      }
      return this.props.children;
    }
  }

  function canUseWebGL() {
    if (faultMode() === "webgl-unavailable") return false;
    try {
      const canvas = document.createElement("canvas");
      const context =
        canvas.getContext("webgl2", { failIfMajorPerformanceCaveat: true })
        || canvas.getContext("webgl", { failIfMajorPerformanceCaveat: true });
      if (!context) return false;
      context.getExtension("WEBGL_lose_context")?.loseContext();
      return true;
    } catch {
      return false;
    }
  }

  function createRenderer(options, name = "three-visual") {
    if (!canUseWebGL()) {
      return {
        ok: false,
        error: new Error(`WebGL is unavailable for ${name}`),
      };
    }
    if (faultMode() === "renderer-init") {
      return {
        ok: false,
        error: new Error(`Forced renderer initialization failure for ${name}`),
      };
    }
    try {
      return { ok: true, renderer: new THREE.WebGLRenderer(options) };
    } catch (error) {
      return { ok: false, error };
    }
  }

  function createVisualLifecycle(element, { onFailure, onStateChange } = {}) {
    const media = window.matchMedia?.("(prefers-reduced-motion: reduce)");
    const state = {
      visible: true,
      hidden: document.hidden,
      reducedMotion: Boolean(media?.matches),
      failed: false,
    };
    const subscribers = new Set();
    const isActive = () =>
      state.visible && !state.hidden && !state.reducedMotion && !state.failed;
    const notify = () => {
      onStateChange?.({ ...state, active: isActive() });
      subscribers.forEach((subscriber) => subscriber(isActive(), state));
    };
    const fail = (error) => {
      if (state.failed) return;
      state.failed = true;
      onFailure?.(error);
      notify();
    };
    const onVisibility = () => {
      state.hidden = document.hidden;
      notify();
    };
    const onMotion = (event) => {
      state.reducedMotion = event.matches;
      notify();
    };
    const observer = "IntersectionObserver" in window
      ? new IntersectionObserver(([entry]) => {
          state.visible = entry?.isIntersecting ?? true;
          notify();
        }, { threshold: 0.01 })
      : null;
    observer?.observe(element);
    document.addEventListener("visibilitychange", onVisibility);
    media?.addEventListener?.("change", onMotion);

    return {
      state,
      isActive,
      fail,
      subscribe(subscriber) {
        subscribers.add(subscriber);
        subscriber(isActive(), state);
        return () => subscribers.delete(subscriber);
      },
      dispose() {
        observer?.disconnect();
        document.removeEventListener("visibilitychange", onVisibility);
        media?.removeEventListener?.("change", onMotion);
        subscribers.clear();
      },
    };
  }

  function createSafeAnimationLoop({
    name = "three-visual",
    lifecycle,
    step,
    onError,
    enabled: initialEnabled = true,
  }) {
    let raf = 0;
    let running = false;
    let disposed = false;
    let frames = 0;
    let enabled = initialEnabled;

    const stop = () => {
      running = false;
      if (raf) cancelAnimationFrame(raf);
      raf = 0;
    };
    const frame = (time) => {
      raf = 0;
      if (disposed || !enabled || !lifecycle.isActive()) {
        stop();
        return;
      }
      try {
        frames += 1;
        if (faultMode() === "animation" && frames >= 2) {
          throw new Error(`Forced animation-loop failure for ${name}`);
        }
        step(time);
      } catch (error) {
        stop();
        lifecycle.fail(error);
        onError?.(error);
        return;
      }
      raf = requestAnimationFrame(frame);
    };
    const start = () => {
      if (disposed || running || !enabled || !lifecycle.isActive()) return;
      running = true;
      raf = requestAnimationFrame(frame);
    };
    const unsubscribe = lifecycle.subscribe((active) => {
      if (active) start();
      else stop();
    });

    return {
      start,
      stop,
      setEnabled(nextEnabled) {
        enabled = Boolean(nextEnabled);
        if (enabled) start();
        else stop();
      },
      dispose() {
        disposed = true;
        stop();
        unsubscribe();
      },
    };
  }

  function watchContextLoss(canvas, lifecycle, onLostCleanup) {
    const onLost = (event) => {
      event.preventDefault();
      lifecycle.fail(new Error("WebGL context lost"));
      onLostCleanup?.();
    };
    canvas.addEventListener("webglcontextlost", onLost, { passive: false });
    return () => canvas.removeEventListener("webglcontextlost", onLost);
  }

  function disposeMaterial(material) {
    if (!material) return;
    for (const value of Object.values(material)) {
      if (value?.isTexture) value.dispose();
    }
    material.dispose?.();
  }

  function disposeThreeTree(root) {
    root?.traverse?.((object) => {
      object.geometry?.dispose?.();
      const materials = Array.isArray(object.material)
        ? object.material
        : [object.material];
      materials.filter(Boolean).forEach(disposeMaterial);
    });
  }

  function disposeRenderer(renderer) {
    renderer?.renderLists?.dispose?.();
    renderer?.dispose?.();
    const canvas = renderer?.domElement;
    canvas?.parentNode?.removeChild(canvas);
  }

  return {
    VisualErrorBoundary,
    VisualFallback,
    canUseWebGL,
    createRenderer,
    createVisualLifecycle,
    createSafeAnimationLoop,
    watchContextLoss,
    disposeThreeTree,
    disposeRenderer,
    faultMode,
  };
}
